import { useEffect, useState } from "react";
import ApplyLeaveForm from "./components/ApplyLeaveForm";
import PendingTab from "./components/PendingTab";
import HistoryTab from "./components/HistoryTab";
import { getLeaveBalance } from "../../api/api";
import { useNavigate } from "react-router-dom";

// ─────────────────────────────────────────────────────────────
// RESPONSIVE HOOK
// ─────────────────────────────────────────────────────────────

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < 768
  );

  useEffect(() => {
    const handler = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handler);

    return () => {
      window.removeEventListener("resize", handler);
    };
  }, []);

  return isMobile;
}

// ─────────────────────────────────────────────────────────────
// SPINNER ANIMATION
// ─────────────────────────────────────────────────────────────

if (
  typeof document !== "undefined" &&
  !document.getElementById("__leave-spin")
) {
  const s = document.createElement("style");

  s.id = "__leave-spin";

  s.innerHTML = `
    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }

      100% {
        transform: rotate(360deg);
      }
    }
  `;

  document.head.appendChild(s);
}

// ─────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────

export default function LeaveApply() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("apply");
  const [leaveType, setLeaveType] = useState("leave");
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);

  // ===========================================================
  // HANDLE BROWSER BACK
  // ===========================================================

  useEffect(() => {
    const handlePopState = () => {
      if (window.history.length <= 1) {
        navigate("/", {
          replace: true,
        });
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate]);

  // ===========================================================
  // FETCH BALANCE
  // ===========================================================

  const fetchBalance = async () => {
    try {
      const data = await getLeaveBalance();

      setBalance(data);
    } catch (error) {
      console.error("Failed to fetch leave balance:", error);
      setBalance(null);
    }
  };

  useEffect(() => {
    fetchBalance();
  }, []);

  // ===========================================================
  // LOADING ANIMATION
  // ===========================================================

  useEffect(() => {
    setLoading(true);

    const timer = setTimeout(() => {
      setLoading(false);
    }, 700);

    return () => {
      clearTimeout(timer);
    };
  }, [activeTab]);

  // ===========================================================
  // BALANCE HELPERS
  // ===========================================================

  const plannedBalance = Number(balance?.planned_balance || 0);

  const unplannedBalance = Number(balance?.unplanned_balance || 0);

  const totalBalance = Number(
    balance?.total_balance ??
      plannedBalance + unplannedBalance
  );

  // ===========================================================
  // CONTACT / CAPTAIN MOBILE NUMBER
  //
  // Backend should return mobile_number when available.
  // If it is empty/null, ApplyLeaveForm will show an editable
  // field so the captain can enter the number.
  // ===========================================================

  const mobileNumber =
    balance?.mobile_number ??
    balance?.captain_mobile_number ??
    "";

  // ===========================================================
  // RENDER CONTENT
  // ===========================================================

  const renderContent = () => {
    if (loading) {
      return (
        <div style={styles.loaderWrapper}>
          <div style={styles.loader} />

          <div style={styles.loaderTitle}>
            Loading Module
          </div>

          <div style={styles.loaderText}>
            Please wait while we prepare your leave dashboard...
          </div>
        </div>
      );
    }

    if (activeTab === "pending") {
      return <PendingTab />;
    }

    if (activeTab === "history") {
      return <HistoryTab />;
    }

    return (
      <ApplyLeaveForm
        refreshBalance={fetchBalance}
        balance={balance}
        plannedBalance={plannedBalance}
        unplannedBalance={unplannedBalance}
        totalBalance={totalBalance}
        mobileNumber={mobileNumber}
      />
    );
  };

  // ===========================================================
  // RENDER
  // ===========================================================

  return (
    <div style={styles.pageWrapper}>
      <div
        style={{
          ...styles.container,
          padding: isMobile ? "16px" : "32px",
        }}
      >
        {/* ================================================= */}
        {/* HEADER */}
        {/* ================================================= */}

        <div
          style={{
            ...styles.pageHeader,
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "flex-start" : "center",
            gap: isMobile ? "12px" : "20px",
          }}
        >
          <div>
            <h1
              style={{
                ...styles.heading,
                fontSize: isMobile ? "24px" : "38px",
              }}
            >
              Leave Management
            </h1>

            <p style={styles.subHeading}>
              Apply, track and manage your leave requests efficiently
            </p>
          </div>
        </div>

        {/* ================================================= */}
        {/* BALANCE CARD */}
        {/* ================================================= */}

        {balance && (
          <div style={styles.balanceCard}>
            <div
              style={{
                ...styles.balanceItem,
                ...getBalanceStyle(plannedBalance),
              }}
            >
              <span style={styles.balanceLabel}>
                Planned
              </span>

              <span style={styles.balanceValue}>
                {plannedBalance}
              </span>
            </div>

            <div
              style={{
                ...styles.balanceItem,
                ...getBalanceStyle(unplannedBalance),
              }}
            >
              <span style={styles.balanceLabel}>
                Unplanned
              </span>

              <span style={styles.balanceValue}>
                {unplannedBalance}
              </span>
            </div>

            <div
              style={{
                ...styles.balanceItem,
                ...getBalanceStyle(totalBalance),
              }}
            >
              <span style={styles.balanceLabel}>
                Total Balance
              </span>

              <span style={styles.balanceValue}>
                {totalBalance}
              </span>
            </div>

            {/* ============================================= */}
            {/* MOBILE NUMBER */}
            {/* ============================================= */}

            {mobileNumber && (
              <div style={styles.mobileInfo}>
                <span style={styles.mobileLabel}>
                  Contact
                </span>

                <span style={styles.mobileValue}>
                  {mobileNumber}
                </span>
              </div>
            )}
          </div>
        )}

        {/* ================================================= */}
        {/* TOP TABS */}
        {/* ================================================= */}

        <div
          style={{
            ...styles.topTabs,
            gap: isMobile ? "8px" : "14px",
          }}
        >
          {[
            {
              key: "apply",
              label: "Apply Leave",
            },
            {
              key: "pending",
              label: "Pending",
            },
            {
              key: "history",
              label: "History",
            },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => {
                setLoading(true);
                setActiveTab(tab.key);
              }}
              style={{
                ...styles.tab,
                ...(activeTab === tab.key
                  ? styles.activeTab
                  : {}),
                flex: isMobile ? 1 : "unset",
                padding: isMobile
                  ? "10px 8px"
                  : "14px 24px",
                fontSize: isMobile ? "12px" : "14px",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ================================================= */}
        {/* MAIN CARD */}
        {/* ================================================= */}

        <div
          style={{
            ...styles.card,
            padding: isMobile ? "0px" : "30px",
          }}
        >
          {activeTab === "apply" && (
            <div style={styles.typeTabs}>
              <button
                type="button"
                style={{
                  ...styles.typeTab,
                  ...styles.activeTypeTab,
                }}
                onClick={() => setLeaveType("leave")}
              >
                Leave Application
              </button>
            </div>
          )}

          <div style={styles.content}>
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================
// BALANCE CARD STYLE
// =============================================================

function getBalanceStyle(value) {
  const numericValue = Number(value || 0);

  if (numericValue <= 0) {
    return {
      background: "#fef2f2",
      border: "1px solid #fecaca",
    };
  }

  if (numericValue < 1) {
    return {
      background: "#fff7ed",
      border: "1px solid #fed7aa",
    };
  }

  return {
    background: "#f3f4f6",
    border: "1px solid #e5e7eb",
  };
}

// =============================================================
// STYLES
// =============================================================

const styles = {
  pageWrapper: {
    minHeight: "100vh",
    background:
      "linear-gradient(135deg,#f8fbff,#eef4ff)",
    position: "relative",
    overflow: "hidden",
  },

  bgCircle1: {
    position: "absolute",
    width: "350px",
    height: "350px",
    borderRadius: "50%",
    background: "rgba(37,99,235,0.08)",
    top: "-120px",
    right: "-120px",
    filter: "blur(10px)",
  },

  bgCircle2: {
    position: "absolute",
    width: "260px",
    height: "260px",
    borderRadius: "50%",
    background: "rgba(124,58,237,0.08)",
    bottom: "-100px",
    left: "-100px",
    filter: "blur(10px)",
  },

  container: {
    position: "relative",
    zIndex: 2,
    maxWidth: "1400px",
    margin: "0 auto",
    boxSizing: "border-box",
  },

  pageHeader: {
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap",
    marginBottom: "24px",
  },

  heading: {
    fontWeight: "800",
    color: "#111827",
    margin: 0,
    letterSpacing: "-1px",
  },

  subHeading: {
    marginTop: "8px",
    fontSize: "14px",
    color: "#6b7280",
    lineHeight: "22px",
  },

  // =========================================================
  // BALANCE CARD
  // =========================================================

  balanceCard: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "16px",
    padding: "16px",
    marginBottom: "24px",
    display: "flex",
    flexWrap: "wrap",
    gap: "12px",
    boxShadow:
      "0 10px 24px rgba(0,0,0,0.05)",
  },

  balanceItem: {
    padding: "12px 18px",
    borderRadius: "12px",
    fontSize: "14px",
    fontWeight: "700",
    color: "#111827",
    minWidth: "140px",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    transition: "0.2s ease",
  },

  balanceLabel: {
    fontSize: "12px",
    color: "#6b7280",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },

  balanceValue: {
    fontSize: "20px",
    fontWeight: "800",
    color: "#111827",
  },

  mobileInfo: {
    padding: "12px 18px",
    borderRadius: "12px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    minWidth: "160px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    textAlign: "center",
  },

  mobileLabel: {
    fontSize: "12px",
    color: "#6b7280",
    fontWeight: "600",
  },

  mobileValue: {
    marginTop: "3px",
    fontSize: "14px",
    color: "#111827",
    fontWeight: "700",
  },

  // =========================================================
  // TOP TABS
  // =========================================================

  topTabs: {
    display: "flex",
    marginBottom: "24px",
    flexWrap: "nowrap",
  },

  tab: {
    borderRadius: "14px",
    border: "1px solid #dbe4f0",
    background: "rgba(255,255,255,0.75)",
    color: "#374151",
    cursor: "pointer",
    fontWeight: "700",
    transition: "0.25s ease",
    backdropFilter: "blur(10px)",
    boxShadow:
      "0 4px 10px rgba(0,0,0,0.04)",
    textAlign: "center",
  },

  activeTab: {
    background:
      "linear-gradient(135deg,#2563eb,#1d4ed8)",
    color: "#fff",
    border: "1px solid #2563eb",
    boxShadow:
      "0 10px 24px rgba(37,99,235,0.25)",
  },

  // =========================================================
  // MAIN CARD
  // =========================================================

  card: {
    background:
      "rgba(255,255,255,0.92)",
    borderRadius: "24px",
    boxShadow:
      "0 20px 50px rgba(15,23,42,0.08)",
    border:
      "1px solid rgba(255,255,255,0.6)",
    backdropFilter: "blur(14px)",
    overflow: "hidden",
  },

  // =========================================================
  // TYPE TAB
  // =========================================================

  typeTabs: {
    display: "flex",
    gap: "16px",
    borderBottom:
      "1px solid #edf2f7",
    marginBottom: "24px",
    paddingBottom: "16px",
  },

  typeTab: {
    padding: "10px 18px",
    borderRadius: "12px",
    border: "none",
    background: "transparent",
    color: "#6b7280",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "700",
  },

  activeTypeTab: {
    background: "#eff6ff",
    color: "#2563eb",
    boxShadow:
      "0 4px 12px rgba(37,99,235,0.12)",
  },

  // =========================================================
  // CONTENT
  // =========================================================

  content: {
    minHeight: "400px",
  },

  // =========================================================
  // LOADER
  // =========================================================

  loaderWrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "80px 0",
    textAlign: "center",
  },

  loader: {
    width: "60px",
    height: "60px",
    border:
      "6px solid rgba(37,99,235,0.15)",
    borderTop:
      "6px solid #2563eb",
    borderRadius: "50%",
    animation:
      "spin 1s linear infinite",
  },

  loaderTitle: {
    marginTop: "20px",
    fontSize: "20px",
    fontWeight: "700",
    color: "#111827",
  },

  loaderText: {
    marginTop: "8px",
    color: "#6b7280",
    fontSize: "13px",
    maxWidth: "300px",
  },
};
